/**
 * Click-to-cycle, written through to the API (T3.7, T3.8).
 *
 * A click does three things, in this order:
 *
 *   1. OPTIMISTIC. The one row changes in a copy of the raw rows and the whole
 *      model is rebuilt with the shared `buildModel`, so every percentage moves
 *      at once. Nothing here does arithmetic on a state value — that is the
 *      rule, and the reason this file imports `buildModel` rather than
 *      summing anything itself.
 *   2. WRITE. One PATCH for that one row. Writes go through a single promise
 *      chain, so they reach the API in the order they were clicked. Each one
 *      sends the ABSOLUTE state the row should have, never "advance by one", so
 *      a retry or a reorder could not double-step a chip.
 *   3. RECONCILE. When the last outstanding write settles, the API's answer is
 *      compared with the local model. The API is authoritative: if the two
 *      disagree, the API's figures are what stays on screen.
 *
 * On failure the failure is shown, the row is restored to the last state the API
 * CONFIRMED for it (not merely the value before the click that failed), and the
 * draft is re-read from the API once the queue has drained. A wrong number is
 * never left standing quietly.
 *
 * A rollback is only safe when it restores the value it actually superseded.
 * Once a later click has touched the same row, the local history is no longer
 * authoritative and the server is, so:
 *   - while other writes to that row are still queued, a failure does NOT roll
 *     the row back — those writes will settle it, and the drain refetch covers
 *     the rest;
 *   - when the last one settles, a failed row goes back to its confirmed state,
 *     which is what the API holds, and never to a value that only ever existed
 *     locally between two clicks.
 *
 * SOMEBODY PUBLISHED UNDERNEATH YOU. A 409 `version_published` is not a failed
 * click: the draft on screen was frozen by another session (or another window
 * of the same person), and the API refused the write rather than let it land in
 * a version nobody can change. The whole screen is stale, so:
 *   - the blueprint is re-read (a new draft exists) and `onRefused` lets the
 *     route stop naming the old version and keep the notice;
 *   - every write still queued for that version is skipped, not sent — each
 *     would be refused the same way — and is listed with the one that was refused;
 *   - the refused edits are NOT re-applied to the new draft. The person is told
 *     which they were and decides. Reason: each click meant "step this chip on
 *     from what I could see", and what they could see is no longer the draft;
 *     the other session may have changed the same row. Writing it into a
 *     version they have not looked at, unprompted, is the same kind of silent
 *     surprise this bug was about. It costs them one click each, and the list
 *     names them.
 * The per-row rollback rule above still applies to that path unchanged.
 *
 * Everything is derived from the cached payload and React state. There is no
 * document.querySelector and no element patching: that is the generator's
 * click-handler defect (CLAUDE.md, Known gotchas), and the same payload feeds
 * the timeline, so it cannot go stale after an edit. `schedule`, `holidays` and
 * `leave` are siblings of `model` in the payload and are spread through
 * untouched on every rebuild, so there is no path by which `leave` gets dropped.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  STATE_ORDER,
  buildModel,
  isDeliverableState,
  stateScaleFromMeta,
  type BlueprintModel,
  type DeliverableRow,
  type DeliverableState,
  type WarningTerms,
} from '@blueprint/shared';
import { ApiError, api } from '../../lib/api';
import { blueprintKeys, type VersionPayload } from '../../lib/blueprints';
import { useTerms } from '../../lib/terms';
import { stateTermKey } from './stateTerms';

/** The API's row id rides alongside the workbook fields; the workbook has no such column. */
type RowWithId = DeliverableRow & { id?: string };

export interface EditFailure {
  /** Full deliverable name — never truncated, per the product requirement. */
  name: string;
  message: string;
}

/** Edits refused because the version they were made against had been published. */
export interface MovedOn {
  /** The version that refused. The screen has since moved off it. */
  versionId: string;
  /** Each refused edit: the full deliverable name and the state it was trying to set. */
  refused: { index: number; name: string; state: DeliverableState }[];
}

/** Thrown for a write skipped because its version was already found to be published. */
class SupersededError extends Error {}

export interface DraftEditor {
  /** Advance one chip. `index` is the `__i` buildModel stamped on the row. */
  cycle: (index: number) => void;
  /** Writes sent and not yet answered. */
  pending: number;
  /** Writes the API accepted since this screen opened. */
  saved: number;
  /** Set after a write was refused; cleared by `dismissFailure` or the next click. */
  failure: EditFailure | null;
  dismissFailure: () => void;
}

/** The answer to a PATCH. See `readWriteResponse` for why this is parsed defensively. */
interface WriteResponse {
  model?: BlueprintModel;
}

/** Next state on the cycle. An unrecognised state is shown as Draft, so it steps to Manuscript. */
export function nextState(current: string): DeliverableState {
  const at = isDeliverableState(current) ? STATE_ORDER.indexOf(current) : 0;
  return STATE_ORDER[(at + 1) % STATE_ORDER.length] as DeliverableState;
}

/**
 * Rebuild the model with one row changed. The rows are COPIED first, because
 * `buildModel` stamps `__i` onto the rows it is given and the cached payload
 * must not be mutated in place — React Query compares by reference.
 */
function withState(
  payload: VersionPayload,
  index: number,
  state: DeliverableState,
  warningTerms: WarningTerms,
): VersionPayload {
  const { raw } = payload.model;
  const deliverables = raw.deliverables.map((d) => ({ ...d }));
  const target = deliverables[index];
  if (!target) return payload;
  target.State = state;

  const model = buildModel(
    raw.meta,
    raw.rooms.map((r) => ({ ...r })),
    raw.pillars.map((p) => ({ ...p })),
    deliverables,
    warningTerms,
    // T7.12. The optimistic rebuild must use the SAME scale the server used, or
    // every figure flickers to the 0/40/90/100 default and then back on
    // reconcile. The scale is the version's own, carried in its Meta
    // (design.md § 10a, 7.9).
    stateScaleFromMeta(raw.meta),
  );
  return { ...payload, model };
}

/** Same ids, same order, same state — the only question reconcile asks about rows. */
function sameFigures(a: BlueprintModel, b: BlueprintModel): boolean {
  return (
    a.overallPct === b.overallPct &&
    a.rooms.length === b.rooms.length &&
    a.rooms.every((r, i) => r.pct === b.rooms[i]?.pct) &&
    a.raw.deliverables.length === b.raw.deliverables.length &&
    a.raw.deliverables.every((d, i) => d.State === b.raw.deliverables[i]?.State)
  );
}

export function useDraftEditor({
  queryKey,
  versionId,
  enabled,
  onRefused,
}: {
  queryKey: QueryKey;
  versionId: string;
  /** False for a viewer or a published version: then `cycle` does nothing at all. */
  enabled: boolean;
  /**
   * Called for every edit refused because its version was published underneath
   * the editor; `first` is true for the first refusal from that version. The
   * caller owns the resulting notice: leaving the address of the frozen version
   * unmounts this hook while the new draft loads, so state kept here would be
   * lost with it, and the person would never be told.
   */
  onRefused?: (versionId: string, edit: MovedOn['refused'][number], first: boolean) => void;
}): DraftEditor {
  const queryClient = useQueryClient();
  const terms = useTerms();

  // The terms live in a ref so `cycle` keeps a stable identity and a click
  // always uses the wording current at that moment.
  const warningTerms = useRef<WarningTerms>({
    room: { singular: '', plural: '' },
    pillar: { singular: '', plural: '' },
    deliverable: { singular: '', plural: '' },
    draftState: '',
  });
  warningTerms.current = {
    room: { singular: terms.noun('room'), plural: terms.plural('room') },
    pillar: { singular: terms.noun('pillar'), plural: terms.plural('pillar') },
    deliverable: { singular: terms.noun('deliverable'), plural: terms.plural('deliverable') },
    draftState: terms.state(stateTermKey('Draft')),
  };

  // Per row while it has writes outstanding: the state the API last confirmed,
  // and how many writes to it are queued or in flight. See the header note.
  const rows = useRef(new Map<number, { confirmed: DeliverableState; inflight: number }>());

  const chain = useRef<Promise<void>>(Promise.resolve());
  const outstanding = useRef(0);
  const anyFailed = useRef(false);
  const [pending, setPending] = useState(0);
  const [saved, setSaved] = useState(0);
  const [failure, setFailure] = useState<EditFailure | null>(null);
  // Versions the API has said are published. Keyed by id, so moving to the new
  // draft (a different id) needs no reset.
  const dead = useRef(new Set<string>());
  const onRefusedRef = useRef(onRefused);
  onRefusedRef.current = onRefused;

  const read = useCallback(
    () => queryClient.getQueryData<VersionPayload>(queryKey),
    [queryClient, queryKey],
  );
  const write = useCallback(
    (next: VersionPayload) => queryClient.setQueryData<VersionPayload>(queryKey, next),
    [queryClient, queryKey],
  );

  // Leaving with a write in flight would lose it silently.
  useEffect(() => {
    if (pending === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending]);

  const settle = useCallback(() => {
    outstanding.current -= 1;
    setPending(outstanding.current);
    if (outstanding.current > 0) return;
    // Queue drained. If anything was refused, the screen may disagree with the
    // database in ways a single rollback cannot know about, so ask it.
    if (anyFailed.current) {
      anyFailed.current = false;
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, queryKey]);

  const cycle = useCallback(
    (index: number) => {
      if (!enabled || dead.current.has(versionId)) return;
      const payload = read();
      const row = payload?.model.raw.deliverables[index] as RowWithId | undefined;
      if (!payload || !row) return;

      const previous = row.State;
      const name = String(row.DeliverableName ?? '');
      const id = row.id;
      const next = nextState(previous);

      // The first write to a row records what the API holds for it right now.
      const entry = rows.current.get(index) ?? {
        confirmed: isDeliverableState(previous) ? previous : 'Draft',
        inflight: 0,
      };
      entry.inflight += 1;
      rows.current.set(index, entry);

      setFailure(null);
      write(withState(payload, index, next, warningTerms.current));

      outstanding.current += 1;
      setPending(outstanding.current);

      chain.current = chain.current.then(async () => {
        try {
          if (dead.current.has(versionId)) throw new SupersededError();
          if (!id) throw new Error('This row has no identifier, so it cannot be saved.');
          const response = await api.patch<WriteResponse>(
            `/versions/${versionId}/deliverables/${id}`,
            { state: next },
          );
          setSaved((n) => n + 1);
          entry.confirmed = next;

          // Only the LAST write to settle reconciles. An earlier response
          // describes a state the screen has already moved past.
          if (outstanding.current === 1) reconcile(response);
        } catch (e) {
          anyFailed.current = true;
          const superseded =
            e instanceof SupersededError || (e instanceof ApiError && e.isVersionPublished);

          if (superseded) {
            noteSuperseded(versionId, { index, name, state: next });
          } else {
            const message = e instanceof Error ? e.message : 'The change was not saved.';
            setFailure({ name, message });
          }

          // Restore the row only when nothing else is still queued for it, and
          // then to the state the API confirmed — see the header note. This holds
          // for a superseded version too: if the re-read fails, the stale draft
          // must not keep showing a value the API refused.
          if (entry.inflight === 1) {
            const current = read();
            const now = current?.model.raw.deliverables[index];
            if (current && now && now.State !== entry.confirmed) {
              write(withState(current, index, entry.confirmed, warningTerms.current));
            }
          }
        } finally {
          entry.inflight -= 1;
          if (entry.inflight === 0) rows.current.delete(index);
          settle();
        }
      });
    },
    // `reconcile` is declared below and only closes over stable refs and callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, read, write, settle, versionId],
  );

  function noteSuperseded(
    refusedVersion: string,
    edit: { index: number; name: string; state: DeliverableState },
  ) {
    const first = !dead.current.has(refusedVersion);
    dead.current.add(refusedVersion);
    setFailure(null);
    onRefusedRef.current?.(refusedVersion, edit, first);
    if (first) {
      // A new draft exists. Re-read everything about this blueprint.
      void queryClient.invalidateQueries({ queryKey: blueprintKeys.all });
    }
  }

  function reconcile(response: WriteResponse | undefined) {
    const authoritative = readWriteResponse(response);
    const current = read();
    if (!authoritative || !current) return;

    if (sameFigures(current.model, authoritative)) return; // Nothing to correct.

    console.warn(
      'Blueprint: the API\'s figures differ from the locally computed ones; showing the API\'s.',
    );
    write({ ...current, model: authoritative });
  }

  return {
    cycle,
    pending,
    saved,
    failure,
    dismissFailure: () => setFailure(null),
  };
}

/**
 * The PATCH response, validated only as far as reconcile needs it. If the API
 * answers with no usable model, reconcile has nothing to compare and the local
 * model stands — the post-drain refetch is the safety net in that case.
 */
function readWriteResponse(response: WriteResponse | undefined): BlueprintModel | null {
  const model = response?.model;
  if (!model || !Array.isArray(model.rooms) || !model.raw) return null;
  return model;
}
